/**
 * Site Visit Reports panel — date-filterable list of submitted visit reports
 * with expandable AI narratives, share-to-clipboard, and CSV download.
 *
 * Two scopes:
 *   - "mine" → /api/site-visits/reports/mine — agent's own reports
 *   - "team" → /api/site-visits/reports/team — every agent (manager+ only)
 *
 * The same component powers both — the parent picks which `scope` to render.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  ClipboardList, Calendar, Download, Share2, ChevronDown, ChevronRight,
  RefreshCw, MapPin, CheckCircle2, XCircle, Edit3, History, X,
  MessageSquare, AlertTriangle, Send,
} from 'lucide-react';
import { toast } from 'sonner';
import { Textarea } from './ui/textarea';
import HelpInfo from './HelpInfo';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDuration = (s) => {
  if (s == null || s < 0) return '0m';
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  if (h) return `${h}h ${m % 60}m`;
  return `${m}m`;
};

const fmtDateTime = (raw) => {
  if (!raw) return '—';
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

/**
 * `refreshKey` — bump this number from a parent to force an immediate
 * re-fetch. Used so a report submitted elsewhere on the page (the
 * post-visit dialog) shows up here within a second, without the agent
 * having to hit Refresh.
 */
const SiteVisitReportsPanel = ({ scope = 'mine', title, refreshKey = 0 }) => {
  const { token } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [from, setFrom] = useState(daysAgoISO(7));
  const [to, setTo] = useState(todayISO());
  const [expandedId, setExpandedId] = useState(null);
  // Inline feedback composer for the team view — lets a CEO / HOD
  // message the visit's author (agent or HOD) straight from the row.
  const [feedbackId, setFeedbackId] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSending, setFeedbackSending] = useState(false);
  // editingVisit is the visit row currently being edited; when set we mount
  // the editor dialog on top with prefilled fields.
  const [editingVisit, setEditingVisit] = useState(null);
  // Active filter tab driven by the count tiles. 'all' shows everything,
  // 'submitted' / 'skipped' narrow the list, 'onsite' filters to visits
  // with real duration on site (useful for ignoring drive-bys).
  const [activeFilter, setActiveFilter] = useState('all');

  const fetchReports = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ token });
      if (from) params.set('date_from', new Date(`${from}T00:00:00`).toISOString());
      if (to) params.set('date_to', new Date(`${to}T23:59:59`).toISOString());
      const res = await fetch(`${API}/api/site-visits/reports/${scope}?${params}`);
      const data = await res.json();
      if (data.success) setReports(data.reports || []);
      else toast.error(data.detail || 'Could not load reports');
    } catch (e) {
      toast.error(`Load failed: ${e.message || e}`);
    } finally {
      setLoading(false);
    }
    // `refreshKey` is an intentional dependency — when the parent bumps it
    // (e.g. after a report is submitted), this callback's identity changes
    // and the effect below re-runs the fetch.
  }, [token, scope, from, to, refreshKey]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const counts = useMemo(() => {
    const total = reports.length;
    const submitted = reports.filter((r) => !!r.report_submitted_at).length;
    const stillOnSite = reports.filter((r) => r.still_on_site).length;
    return { total, submitted, stillOnSite };
  }, [reports]);

  // Apply the active filter to the visible row list. "Skipped" and
  // "Forgot in/out" used to live here as their own filters — those
  // accountability angles were removed per product decision; visits are
  // simply submitted or pending now.
  const visibleReports = useMemo(() => {
    if (activeFilter === 'submitted') return reports.filter((r) => !!r.report_submitted_at);
    if (activeFilter === 'still') return reports.filter((r) => r.still_on_site);
    return reports;
  }, [reports, activeFilter]);

  const downloadCsv = () => {
    if (!token) return;
    const params = new URLSearchParams({ token, scope });
    if (from) params.set('date_from', new Date(`${from}T00:00:00`).toISOString());
    if (to) params.set('date_to', new Date(`${to}T23:59:59`).toISOString());
    window.open(`${API}/api/site-visits/reports/export.csv?${params}`, '_blank');
  };

  // Send feedback / a comment to the visit's author (agent or HOD).
  // Posts to the direct-message endpoint so it lands in the
  // recipient's "Feedback from Management" feed.
  const sendVisitFeedback = async (report) => {
    const targetId = report.agent_id;
    if (!targetId) { toast.error('This visit has no linked author to message'); return; }
    if (!feedbackText.trim()) { toast.error('Write feedback before sending'); return; }
    setFeedbackSending(true);
    try {
      const res = await fetch(`${API}/api/hod/agents/${targetId}/direct-message`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ feedback_text: feedbackText, agent_id: targetId }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        toast.error(data?.error || data?.detail || 'Failed to send');
        return;
      }
      toast.success(`Feedback sent to ${report.agent_name || 'author'}`);
      setFeedbackId(null);
      setFeedbackText('');
    } catch (e) {
      toast.error(`Send failed: ${e.message || e}`);
    } finally {
      setFeedbackSending(false);
    }
  };

  const shareReport = async (report) => {
    const lines = [
      `Site visit report — ${report.property_name}`,
      `Agent: ${report.agent_name || '—'}`,
      `Arrived: ${fmtDateTime(report.arrival_time)}`,
      `Departed: ${fmtDateTime(report.departure_time)}`,
      `Duration: ${fmtDuration(report.duration_seconds)}`,
      `Outcome: ${report.report_outcome || '—'}`,
      `Productive: ${report.report_productive == null ? '—' : (report.report_productive ? 'yes' : 'no')}`,
      '',
      'Notes:',
      report.report_notes || '(none)',
      '',
      'Next steps:',
      report.report_next_steps || '(none)',
    ];
    if (report.ai_narrative) {
      lines.push('', '--- AI narrative ---', report.ai_narrative);
    }
    const text = lines.join('\n');
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Site visit report', text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success('Report copied to clipboard');
      }
    } catch (e) {
      toast.error(`Share failed: ${e.message || e}`);
    }
  };

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2 flex-wrap">
          <ClipboardList className="w-4 h-4 text-emerald-600" />
          {title || (scope === 'team' ? 'Team Site Visit Reports' : 'My Site Visit Reports')}
          <HelpInfo
            align="inline"
            title={scope === 'team' ? 'How to read Team Site Visit Reports' : 'How to read My Site Visit Reports'}
            steps={[
              scope === 'team'
                ? <>One row per site visit your team made. Click any row to expand and read the agent's notes, outcome, and the AI narrative.</>
                : <>One row per site visit you've made. Click any row to expand and review the outcome, notes, and AI narrative.</>,
              <>The three tiles up top are clickable filters: <em>Total visits</em> shows everything; <em>Submitted</em> only visits with a filed report; <em>Still on site</em> visits that haven't closed yet.</>,
              <>Badges on each row tell you the channel: <strong>Manual</strong> (you logged via Log Field Visit) vs auto-detected, plus <strong>GPS verified</strong> when location matched the geofence.</>,
              <>Outcome badges (<em>Productive</em> / <em>Not productive</em>) reflect what you selected in the post-visit dialog. The <strong>updates</strong> badge shows how many times the report was edited.</>,
              scope === 'mine'
                ? <>Click <strong>Edit</strong> on any row to update the outcome, notes, or next steps. The original submission is preserved in the history.</>
                : <>Each row has a <strong>Feedback</strong> button (💬) — send a comment straight to the visit's author, whether they're an agent or an HOD.</>,
              'Use the date range pickers and CSV download to export a date-bounded report for review meetings.',
            ]}
            tip="GPS-verified visits with productive outcomes are your highest-trust data points — those are the visits to reference in pipeline reviews."
          />
          <span className="ml-auto flex items-center gap-2 flex-wrap">
            <Calendar size={14} className="text-slate-400" />
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="h-8 text-xs border border-slate-300 rounded px-2"
            />
            <span className="text-xs text-slate-400">to</span>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="h-8 text-xs border border-slate-300 rounded px-2"
            />
            {/* Clear — empties the date fields and resets the tile
                filter to "Total visits". Empty dates fall through the
                `if (from)` / `if (to)` guards in fetchReports, so the
                request goes out without date_from / date_to and the
                backend returns all-time. The button is always visible
                so users can find it without trial and error; it dims
                when there's nothing to clear. */}
            {(() => {
              const atCleared = !from && !to && activeFilter === 'all';
              return (
                <Button
                  variant="ghost"
                  size="sm"
                  className={`h-8 text-xs ${
                    atCleared
                      ? 'text-slate-400 hover:text-rose-600'
                      : 'text-rose-600 hover:text-rose-700'
                  }`}
                  onClick={() => {
                    setFrom('');
                    setTo('');
                    setActiveFilter('all');
                  }}
                  title="Clear date filter and tile selection — show all visits"
                >
                  Clear
                </Button>
              );
            })()}
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={fetchReports} title="Refresh">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={downloadCsv}>
              <Download size={12} className="mr-1" /> CSV
            </Button>
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Clickable filter tiles — each acts as a tab. Active tile gets a
            stronger border and ring so it reads as "selected". */}
        <div className="grid grid-cols-3 gap-2 mb-3 text-center">
          {[
            { k: 'all',       label: 'Total visits',     value: counts.total,       bg: 'bg-slate-50',   border: 'border-slate-200',   active: 'ring-slate-400 border-slate-400',     text: 'text-slate-900',   sub: 'text-slate-500'   },
            { k: 'submitted', label: 'Submitted',        value: counts.submitted,   bg: 'bg-emerald-50', border: 'border-emerald-200', active: 'ring-emerald-500 border-emerald-500', text: 'text-emerald-700', sub: 'text-emerald-700' },
            { k: 'still',     label: 'Still on site',    value: counts.stillOnSite, bg: 'bg-blue-50',    border: 'border-blue-200',    active: 'ring-blue-500 border-blue-500',       text: 'text-blue-700',    sub: 'text-blue-700'    },
          ].map((tile) => (
            <button
              key={tile.k}
              type="button"
              onClick={() => setActiveFilter(tile.k)}
              className={`rounded-md border py-2 text-center cursor-pointer transition ${tile.bg} ${
                activeFilter === tile.k ? `ring-2 ${tile.active}` : tile.border + ' hover:bg-opacity-80'
              }`}
            >
              <div className={`text-xl font-semibold ${tile.text}`}>{tile.value}</div>
              <div className={`text-xs ${tile.sub}`}>{tile.label}</div>
            </button>
          ))}
        </div>

        {visibleReports.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-6">
            {activeFilter === 'all'
              ? 'No site visits in this date range.'
              : `No visits match the "${activeFilter}" filter.`}
          </p>
        ) : (
          <div className="space-y-2">
            {visibleReports.map((r) => {
              const isOpen = expandedId === r.id;
              const submitted = !!r.report_submitted_at;
              const history = Array.isArray(r.report_history) ? r.report_history : [];
              const canEdit = scope === 'mine'; // HOD/team view is read-only
              // Cross-check signals from the backend. `escalate` is the
              // headline flag — the visit's GPS doesn't line up with the
              // property it was claimed against. We outline the row in
              // amber when that happens so the HOD sees it at a glance.
              const escalate = !!r.escalate;
              const gpsMatch = r.gps_match;             // 'verified' | 'mismatch' | 'unknown'
              const visitSource = r.visit_source;        // 'auto' | 'manual'
              const gpsDistance = r.gps_distance_m;
              const rowBorder = escalate
                ? 'border-2 border-amber-400 bg-amber-50/40'
                : 'border border-slate-200';
              return (
                <div key={r.id} className={`rounded-lg ${rowBorder}`}>
                  <button
                    type="button"
                    onClick={() => setExpandedId(isOpen ? null : r.id)}
                    className="w-full text-left p-3 flex items-start gap-3 hover:bg-slate-50"
                  >
                    {isOpen ? <ChevronDown size={16} className="mt-0.5 text-slate-400" />
                            : <ChevronRight size={16} className="mt-0.5 text-slate-400" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <MapPin size={14} className="text-emerald-600" />
                        <span className="font-medium text-slate-900 truncate">{r.property_name}</span>
                        {scope === 'team' && (
                          <span className="text-xs text-slate-500">· {r.agent_name}</span>
                        )}
                        {r.still_on_site ? (
                          <Badge className="text-xs bg-blue-100 text-blue-700 border border-blue-300">
                            <MapPin size={10} className="mr-0.5" /> Still on site
                          </Badge>
                        ) : submitted ? (
                          <Badge className="text-xs bg-emerald-100 text-emerald-700 border border-emerald-300">
                            <CheckCircle2 size={10} className="mr-0.5" /> Report Submitted
                          </Badge>
                        ) : null}
                        {r.report_outcome && (
                          <Badge className="text-xs bg-slate-100 text-slate-700 border-slate-300 border">
                            {r.report_outcome}
                          </Badge>
                        )}
                        {r.report_productive === true && (
                          <Badge className="text-xs bg-emerald-50 text-emerald-700 border-emerald-300 border">
                            <CheckCircle2 size={10} className="mr-0.5" /> Productive
                          </Badge>
                        )}
                        {r.report_productive === false && (
                          <Badge className="text-xs bg-amber-50 text-amber-700 border-amber-300 border">
                            <XCircle size={10} className="mr-0.5" /> Not productive
                          </Badge>
                        )}
                        {history.length > 1 && (
                          <Badge className="text-xs bg-violet-50 text-violet-700 border-violet-300 border">
                            <History size={10} className="mr-0.5" /> {history.length} updates
                          </Badge>
                        )}
                        {/* Source badge — was this visit created from a
                            GPS geofence enter or logged manually by the
                            agent/HOD? Distinguishes the two paths so the
                            HOD knows where the data came from. */}
                        {visitSource === 'auto' && (
                          <Badge className="text-xs bg-sky-50 text-sky-700 border-sky-300 border">
                            <MapPin size={10} className="mr-0.5" /> Auto-detected
                          </Badge>
                        )}
                        {visitSource === 'manual' && (
                          <Badge className="text-xs bg-slate-100 text-slate-700 border-slate-300 border">
                            <Edit3 size={10} className="mr-0.5" /> Manual
                          </Badge>
                        )}
                        {/* GPS cross-check verdict — verified means the
                            arrival GPS sits inside the property's
                            geofence; mismatch means the agent claimed a
                            property they weren't actually at and the row
                            is being escalated to the HOD. */}
                        {gpsMatch === 'verified' && (
                          <Badge className="text-xs bg-emerald-50 text-emerald-700 border-emerald-300 border">
                            <CheckCircle2 size={10} className="mr-0.5" /> GPS verified
                          </Badge>
                        )}
                        {gpsMatch === 'mismatch' && (
                          <Badge className="text-xs bg-amber-100 text-amber-800 border-amber-400 border">
                            ⚠ GPS mismatch{gpsDistance ? ` · ${Math.round(gpsDistance)}m off` : ''}
                          </Badge>
                        )}
                        {gpsMatch === 'unknown' && submitted && (
                          <Badge className="text-xs bg-slate-50 text-slate-500 border-slate-200 border">
                            GPS not recorded
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 font-mono">
                        {submitted
                          ? <>Submitted {fmtDateTime(r.report_submitted_at)}</>
                          : <>Closed {fmtDateTime(r.departure_time)}</>}
                        {r.report_last_edited_at && submitted && r.report_last_edited_at !== r.report_submitted_at && (
                          <> · edited {fmtDateTime(r.report_last_edited_at)}</>
                        )}
                        {' · '}{fmtDuration(r.duration_seconds)} on site
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-wrap">
                      {canEdit && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs px-2"
                          onClick={(e) => { e.stopPropagation(); setEditingVisit(r); }}
                        >
                          <Edit3 size={11} className="mr-1" /> Edit
                        </Button>
                      )}
                      {/* Feedback — team view only (a manager / CEO
                          messaging the author); hidden on the agent's
                          own "mine" view. */}
                      {scope === 'team' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFeedbackId(feedbackId === r.id ? null : r.id);
                            setFeedbackText('');
                          }}
                          title="Send feedback to the author"
                        >
                          <MessageSquare size={13} className={feedbackId === r.id ? 'text-violet-600' : ''} />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={(e) => { e.stopPropagation(); shareReport(r); }}
                        title="Copy to clipboard"
                      >
                        <Share2 size={13} />
                      </Button>
                    </div>
                  </button>
                  {scope === 'team' && feedbackId === r.id && (
                    <div className="px-4 pb-3 pt-2 border-t border-slate-100 bg-violet-50/40 space-y-2">
                      <p className="text-xs font-semibold text-violet-700">
                        Send feedback to {r.agent_name || 'author'}
                      </p>
                      <Textarea
                        value={feedbackText}
                        onChange={(e) => setFeedbackText(e.target.value)}
                        rows={3}
                        placeholder="Write a comment on this site visit…"
                        className="text-xs"
                      />
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => { setFeedbackId(null); setFeedbackText(''); }}
                          disabled={feedbackSending}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          className="h-7 text-xs bg-violet-600 hover:bg-violet-700 text-white"
                          onClick={() => sendVisitFeedback(r)}
                          disabled={feedbackSending || !feedbackText.trim()}
                        >
                          {feedbackSending
                            ? <><RefreshCw size={12} className="mr-1 animate-spin" /> Sending</>
                            : <><Send size={12} className="mr-1" /> Send feedback</>}
                        </Button>
                      </div>
                    </div>
                  )}
                  {isOpen && (
                    <div className="px-4 pb-4 border-t border-slate-100 bg-slate-50/60 text-sm space-y-3 pt-3">
                      {/* Mismatch banner — sits at the top of the expanded
                          body when the agent's recorded GPS doesn't match
                          the geofence of the property they claimed. Tells
                          HOD/CEO the exact distance and offers a one-click
                          escalation that fires a feedback message to the
                          author (via the existing /api/hod/agents/.../
                          direct-message endpoint). */}
                      {escalate && (
                        <div className="rounded-md border border-amber-400 bg-amber-50 p-3 flex items-start gap-2">
                          <AlertTriangle size={14} className="text-amber-700 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-amber-800">
                              GPS doesn't match the claimed property
                            </p>
                            <p className="text-xs text-amber-700 mt-0.5">
                              Recorded location was {gpsDistance ? `${Math.round(gpsDistance)}m` : 'far'} from
                              the {r.property_name} geofence
                              {r.geofence_radius_m ? ` (radius ${Math.round(r.geofence_radius_m)}m)` : ''}.
                              The agent may have logged this visit without actually being on site.
                            </p>
                          </div>
                          {scope === 'team' && r.agent_id && token && (
                            <Button
                              size="sm"
                              className="h-7 text-xs bg-amber-600 hover:bg-amber-500 text-white"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  const msg = `Visit at ${r.property_name}: your recorded GPS was ${
                                    gpsDistance ? Math.round(gpsDistance) + 'm' : 'far'
                                  } from the property geofence. Please clarify where you actually were and re-submit the visit report with the correct details.`;
                                  const res = await fetch(`${API}/api/hod/agents/${r.agent_id}/direct-message`, {
                                    method: 'POST',
                                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ feedback_text: msg, agent_id: r.agent_id }),
                                  });
                                  const data = await res.json();
                                  if (data.success) toast.success(`Escalation sent to ${r.agent_name}`);
                                  else toast.error(data.detail || 'Failed to escalate');
                                } catch (err) {
                                  toast.error(`Escalate failed: ${err.message || err}`);
                                }
                              }}
                            >
                              <AlertTriangle size={11} className="mr-1" /> Escalate to agent
                            </Button>
                          )}
                        </div>
                      )}
                      {r.report_notes && (
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Agent notes</p>
                          <p className="text-slate-700 whitespace-pre-wrap">{r.report_notes}</p>
                        </div>
                      )}
                      {r.report_next_steps && (
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Next steps</p>
                          <p className="text-slate-700 whitespace-pre-wrap">{r.report_next_steps}</p>
                        </div>
                      )}
                      {r.ai_narrative ? (
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-1">AI narrative</p>
                          <div className="text-slate-800 whitespace-pre-wrap rounded-md bg-white border border-emerald-200 p-3 text-sm">
                            {r.ai_narrative}
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-slate-400">AI narrative not generated yet.</p>
                      )}
                      {/* Update history — appears when the report was edited at
                          least once. Newest first. */}
                      {history.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                            <History size={10} className="inline mr-1" />
                            Update history ({history.length})
                          </p>
                          <ol className="space-y-1 text-xs">
                            {history.slice().reverse().map((h, i) => (
                              <li key={i} className="rounded border border-slate-200 bg-white p-2">
                                <div className="flex justify-between text-slate-600">
                                  <span className="font-mono">{fmtDateTime(h.edited_at)}</span>
                                  <span className="uppercase text-[10px] tracking-wide">
                                    {h.kind === 'initial_submission' ? 'First submission' : 'Edit'}
                                  </span>
                                </div>
                                {h.outcome && <p><strong>Outcome:</strong> {h.outcome}</p>}
                                {h.notes && <p className="whitespace-pre-wrap"><strong>Notes:</strong> {h.notes}</p>}
                                {h.next_steps && <p className="whitespace-pre-wrap"><strong>Next steps:</strong> {h.next_steps}</p>}
                              </li>
                            ))}
                          </ol>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {editingVisit && (
        <ReportEditorDialog
          visit={editingVisit}
          token={token}
          onClose={() => setEditingVisit(null)}
          onSaved={() => { setEditingVisit(null); fetchReports(); }}
        />
      )}
    </Card>
  );
};

// NOTE — VisitAccountabilityActions (the "send feedback/warning on a
// skipped or forgot-in/out visit" composer) was removed alongside the
// skipped / forgot-in/out feature. The two reasons it existed for are
// gone, so the component had no remaining call sites.
//
// Editor for an existing visit's report — same fields as the End-Visit
// post-visit dialog, prefilled with whatever's already on the record so
// the agent can edit it later.
const ReportEditorDialog = ({ visit, token, onClose, onSaved }) => {
  const [outcome, setOutcome] = useState(visit.report_outcome || '');
  const [productive, setProductive] = useState(
    visit.report_productive === true ? true
    : visit.report_productive === false ? false
    : null,
  );
  const [notes, setNotes] = useState(visit.report_notes || '');
  const [nextSteps, setNextSteps] = useState(visit.report_next_steps || '');
  const [saving, setSaving] = useState(false);

  const outcomes = [
    'Met client', 'Client no-show', 'Showing completed',
    'Inspection done', 'Met broker', 'Other',
  ];
  const isUpdate = !!visit.report_submitted_at;

  const save = async () => {
    if (!outcome && !notes && productive === null && !nextSteps) {
      toast('Pick an outcome or add a note before saving');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(
        `${API}/api/site-visits/${visit.id}/report?token=${encodeURIComponent(token)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ outcome, productive, notes, next_steps: nextSteps }),
        },
      );
      const data = await res.json();
      if (data.success) {
        toast.success(isUpdate ? 'Report updated' : 'Report submitted');
        onSaved();
      } else {
        toast.error(data.detail || 'Could not save report');
      }
    } catch (e) {
      toast.error(`Save failed: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
        backgroundColor: 'rgba(15, 23, 42, 0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
    >
      <div className="w-full max-w-lg mx-4 bg-white rounded-2xl shadow-2xl border border-slate-200 p-6">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              {isUpdate ? 'Update visit report' : 'Visit report'}
            </h2>
            <p className="text-xs text-slate-500">
              {visit.property_name}
              {visit.duration_seconds != null && <> · {Math.floor(visit.duration_seconds / 60)}m on site</>}
            </p>
          </div>
          <button onClick={onClose} disabled={saving} className="text-slate-400 hover:text-slate-600">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600">Outcome</label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {outcomes.map((o) => (
                <button
                  key={o}
                  type="button"
                  onClick={() => setOutcome(o)}
                  className={`px-2.5 py-1 rounded-full text-xs border ${
                    outcome === o
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  {o}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600">Was this productive?</label>
            <div className="flex gap-2 mt-1">
              {[{ v: true, label: 'Yes' }, { v: false, label: 'No' }].map((opt) => (
                <button
                  key={opt.label}
                  type="button"
                  onClick={() => setProductive(opt.v)}
                  className={`px-3 py-1 rounded-md text-xs border ${
                    productive === opt.v
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600">What happened?</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Brief summary — who you met, key discussion points…"
              className="w-full mt-1 px-3 py-2 rounded-md border border-slate-300 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600">Next steps (optional)</label>
            <textarea
              value={nextSteps}
              onChange={(e) => setNextSteps(e.target.value)}
              rows={2}
              placeholder="Send proposal, schedule follow-up, share floor plans…"
              className="w-full mt-1 px-3 py-2 rounded-md border border-slate-300 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>
        </div>

        <div className="flex gap-2 justify-end mt-5">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SiteVisitReportsPanel;
